import { Box, Select, MenuItem } from '@mui/material'
import {
  BatchSetting,
  ProductSetting,
} from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.types'
import { GridColDef } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import {
  batchRowCalculations,
  useSettingsContext,
} from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import { toast } from 'react-hot-toast'
import {
  useUpdateBatchSettings,
  useUploadBatchSettings,
} from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.data'

type BatchSettingsTableProps = {
  data: BatchSetting[]
  isLoading?: boolean
  productSettings?: ProductSetting[]
}

export const BatchSettingsTable = ({
  data,
  isLoading,
}: BatchSettingsTableProps) => {
  //const [isOpen, setIsOpen] = useState<boolean>(false)
  const COLUMN_WIDTH = 150

  const onSuccess = () => {
    toast.success('Update Succesful')
  }

  const onError = () => {
    toast.error('Update Failed')
  }

  const { mutate, isLoading: isSaving } = useUpdateBatchSettings(
    onSuccess,
    onError
  )

  const batchSettingColumns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: true,
      },
      {
        field: 'workCentreCode',
        headerName: 'Work Centre Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: true,
      },
      {
        field: 'batchGroupingCode',
        headerName: 'Batch Group Code', // Updated display name
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: true,
      },
      {
        field: 'unitOfMeasureCode',
        headerName: 'Unit Of Measure Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'salesRate',
        headerName: 'Sales Rate',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value: number) => {
          return value === null ? '' : value.toFixed(2)
        },
      },
      {
        field: 'minimumBatchSize',
        headerName: 'Min Batch Size', // Updated display name
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
      },
      {
        field: 'minimumBatchSizeIncrement',
        headerName: 'Min Batch Size Increment',
        width: COLUMN_WIDTH,
        type: 'number',
      },
      {
        field: 'naturalCycle',
        headerName: 'Natural Cycle',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value: number) => {
          if (value === null) return ''
          if (value === Infinity) return '-'
          return value.toFixed(3)
        },
        renderCell: (params) => {
          if (params.value === Infinity) {
            return '-'
          }
          return params.formattedValue
        },
      },
      {
        field: 'initialBatchCycle',
        headerName: 'Initial Batch Cycle',
        width: COLUMN_WIDTH,
        type: 'number',
      },
      {
        field: 'minBatchCycleOverride',
        headerName: 'Min Batch Cycle Override',
        width: COLUMN_WIDTH,
        type: 'singleSelect',
        editable: true,
        valueOptions: [1, 2, 4, 8],
        renderEditCell: (params) => {
          return (
            <Select
              value={params.value || ''}
              onChange={async (event) => {
                const newValue = event.target.value
                const isValid = await params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: newValue,
                })

                if (isValid) {
                  const cellMode = params.api.getCellMode(
                    params.id,
                    params.field
                  )
                  if (cellMode === 'edit') {
                    params.api.stopCellEditMode({
                      id: params.id,
                      field: params.field,
                    })
                  }
                }
              }}
              onClose={(_event) => {
                const cellMode = params.api.getCellMode(params.id, params.field)
                if (cellMode === 'edit') {
                  params.api.stopCellEditMode({
                    id: params.id,
                    field: params.field,
                  })
                }
              }}
              sx={{
                width: '100%',
                minWidth: COLUMN_WIDTH,
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
              size="small"
              open={true}
              variant="outlined"
            >
              {params.colDef.valueOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          )
        },
      },
      {
        field: 'maxBatchCycleOverride',
        headerName: 'Max Batch Cycle Override',
        width: COLUMN_WIDTH,
        type: 'singleSelect',
        editable: true,
        valueOptions: [1, 2, 4, 8],
        renderEditCell: (params) => {
          return (
            <Select
              value={params.value || ''}
              onChange={async (event) => {
                const newValue = event.target.value
                const isValid = await params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: newValue,
                })

                if (isValid) {
                  const cellMode = params.api.getCellMode(
                    params.id,
                    params.field
                  )
                  if (cellMode === 'edit') {
                    params.api.stopCellEditMode({
                      id: params.id,
                      field: params.field,
                    })
                  }
                }
              }}
              onClose={(_event) => {
                const cellMode = params.api.getCellMode(params.id, params.field)
                if (cellMode === 'edit') {
                  params.api.stopCellEditMode({
                    id: params.id,
                    field: params.field,
                  })
                }
              }}
              sx={{
                width: '100%',
                minWidth: COLUMN_WIDTH,
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
              size="small"
              open={true}
              variant="outlined"
            >
              {params.colDef.valueOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          )
        },
      },
      {
        field: 'minBatchCycleInput',
        headerName: 'Min Batch Cycle Input',
        width: COLUMN_WIDTH,
        type: 'number',
      },
      {
        field: 'maxBatchCycleInput',
        headerName: 'Max Batch Cycle Input',
        width: COLUMN_WIDTH,
        type: 'number',
      },
      {
        field: 'replenishmentTypeCode',
        headerName: 'Replenishment Type Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'isOverriden',
        headerName: 'Is Overriden', // Matching display name from config
        width: COLUMN_WIDTH,
        type: 'boolean',
      },
      // Commented out hidden fields
      // {
      //   field: 'currentBatchCycleTime',
      //   headerName: 'Current Batch Cycle Time',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true, // Setting visible to FALSE
      // },
      // {
      //   field: 'currentBatchStartWeek',
      //   headerName: 'Current Batch Cycle Start Week', // Updated display name
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true, // Setting visible to FALSE
      // },
      // {
      //   field: 'fixedBatchStartWeek',
      //   headerName: 'Fixed Batch Cycle Start Week', // Updated display name
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true, // Setting visible to FALSE
      // },
      {
        field: 'isIncluded',
        headerName: 'Is Included',
        width: COLUMN_WIDTH,
        type: 'boolean',
      },
    ]
  }, [])

  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state

  const updateTableData = (changedRows: Record<number, BatchSetting>) => {
    mutate({ changedRows, projectId, scenarioId })
  }

  const onUploadSuccess = () => {
    toast.success('Upload Successful')
  }

  const onUploadError = () => {
    toast.error('Upload Failed')
  }

  const { mutate: uploadMutate, isPending: isUploading } =
    useUploadBatchSettings(onUploadSuccess, onUploadError)

  const uploadData = (file: File) => {
    uploadMutate({ file, projectId, scenarioId })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        overflow: 'auto',
        flexDirection: 'column',
      }}
    >
      <BaseTable
        data={data}
        columns={batchSettingColumns}
        isLoading={isLoading ?? false}
        updateTableData={updateTableData}
        processRow={batchRowCalculations}
        isSaving={isSaving}
        checkBoxSelection={true}
        pinnedColumns={[
          'baseSourceSiteCode',
          'workCentreCode',
          'batchGroupingCode',
        ]}
        uploadData={uploadData}
        isUploading={isUploading ?? false}
        customDownloadDataMethod={(tableData) => tableData}
      />
    </Box>
  )
}
